import * as React from 'react';
import { Editor } from 'tinymce';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	BioPlaceholder,
	CityPlaceholder,
	CompanyNamePlaceholder,
	FileRequestLinkPlaceholder,
	FileSendLinkPlaceholder,
	FirstNamePlaceholder,
	FullNamePlaceholder,
	JobTitlePlaceholder,
	LastNamePlaceholder,
	MeetingHomepageLinkPlaceholder,
	SenderEmailPlaceholder,
	SenderFirstNamePlaceholder,
	SenderFullNamePlaceholder,
	SenderPhonePlaceholder,
	StatePlaceholder,
} from '../../../../models/Token';
import { useContactCustomFields } from '../../../../queries/Contact/useContactCustomFields';
import { useContactIntegrationCustomFieldsQuery } from '../../../../queries/Contact/useContactIntegrationCustomFieldsQuery';
import {
	Dropdown,
	DropdownContent,
	DropdownItem,
	DropdownSelectTrigger,
	DropdownSeparator,
	DropdownSubMenu,
	DropdownSubTrigger,
} from '../../../components-v2/Dropdown';
import { styleSheet } from './styles';

interface IMergeFieldsDropdownProps {
	editorRef?: React.MutableRefObject<Editor | null | undefined>;
	onInsert?: (contentHtmlStringValue: string) => void;
	/**
	 * TipTap consumers pass this to insert the bare symbol through their own composer (which renders the
	 * pill and pads it with spaces). When present it takes precedence over the `editorRef` TinyMCE path.
	 */
	onInsertSymbol?: (symbol: string) => void;
	onInteractionStart?: () => void;
	onOpenChange?: (open: boolean) => void;
	canSendFileLinks?: boolean;
	canRequestFiles?: boolean;
}

export function MergeFieldsDropdown({
	editorRef,
	onInsert,
	onInsertSymbol,
	onInteractionStart,
	onOpenChange,
	canSendFileLinks,
	canRequestFiles,
}: IMergeFieldsDropdownProps) {
	const userSession = useUserSession();
	const customFieldsQuery = useContactCustomFields();
	const integrationFieldsQuery = useContactIntegrationCustomFieldsQuery();

	const hasMeetingScheduler = userSession?.account?.features?.meetingScheduler?.enabled;

	const insertMergeField = React.useCallback(
		(symbol: string) => {
			if (onInsertSymbol) {
				onInsertSymbol(symbol);
				return;
			}

			const editor = editorRef?.current;
			if (!editor) {
				return;
			}

			editor.focus();
			editor.insertContent(symbol);
			onInsert?.(editor.getContent() as string);
			requestAnimationFrame(() => {
				try {
					editor.focus();
				} catch {
					// TinyMCE may have been torn down if the node unmounted while the menu was closing.
				}
			});
		},
		[editorRef, onInsert, onInsertSymbol]
	);

	const customFields = customFieldsQuery.data?.fields ?? [];
	const integrationFields = integrationFieldsQuery.data ?? [];

	return (
		<Dropdown modal={false} onOpenChange={onOpenChange}>
			<DropdownSelectTrigger onPointerDownCapture={onInteractionStart} styles={[styleSheet.trigger]}>
				Merge Fields
			</DropdownSelectTrigger>
			<DropdownContent align='start' sideOffset={4} styles={[styleSheet.content]}>
				{/* Contact Submenu */}
				<DropdownSubMenu>
					<DropdownSubTrigger>Contact</DropdownSubTrigger>
					<DropdownContent align='start' sideOffset={4} styles={[styleSheet.subContent]}>
						<DropdownItem onClick={() => insertMergeField(FirstNamePlaceholder.symbol)}>First Name</DropdownItem>
						<DropdownItem onClick={() => insertMergeField(LastNamePlaceholder.symbol)}>Last Name</DropdownItem>
						<DropdownItem onClick={() => insertMergeField(FullNamePlaceholder.symbol)}>Full Name</DropdownItem>
						<DropdownItem onClick={() => insertMergeField(CompanyNamePlaceholder.symbol)}>Company Name</DropdownItem>
						<DropdownItem onClick={() => insertMergeField(JobTitlePlaceholder.symbol)}>Job Title</DropdownItem>
						<DropdownItem onClick={() => insertMergeField(CityPlaceholder.symbol)}>City</DropdownItem>
						<DropdownItem onClick={() => insertMergeField(StatePlaceholder.symbol)}>State/Province</DropdownItem>
						<DropdownItem onClick={() => insertMergeField(BioPlaceholder.symbol)}>Misc</DropdownItem>

						{customFields.length > 0 && (
							<>
								<DropdownSeparator />
								{customFields.map(field => (
									<DropdownItem key={field.name} onClick={() => insertMergeField(`{{${field.label}}}`)}>
										{field.label}
									</DropdownItem>
								))}
							</>
						)}

						{integrationFields.length > 0 && (
							<>
								<DropdownSeparator />
								{integrationFields.map(field => (
									<DropdownItem key={field.value} onClick={() => insertMergeField(field.symbol)}>
										{field.text}
									</DropdownItem>
								))}
							</>
						)}
					</DropdownContent>
				</DropdownSubMenu>

				{/* Sender Submenu */}
				<DropdownSubMenu>
					<DropdownSubTrigger>Sender</DropdownSubTrigger>
					<DropdownContent align='start' sideOffset={4} styles={[styleSheet.subContent]}>
						<DropdownItem onClick={() => insertMergeField(SenderFirstNamePlaceholder.symbol)}>First Name</DropdownItem>
						<DropdownItem onClick={() => insertMergeField(SenderFullNamePlaceholder.symbol)}>Full Name</DropdownItem>
						<DropdownItem onClick={() => insertMergeField(SenderEmailPlaceholder.symbol)}>Email</DropdownItem>
						<DropdownItem onClick={() => insertMergeField(SenderPhonePlaceholder.symbol)}>Phone Number</DropdownItem>
					</DropdownContent>
				</DropdownSubMenu>

				{/* Meetings Submenu - only if feature enabled */}
				{hasMeetingScheduler && (
					<DropdownSubMenu>
						<DropdownSubTrigger>Meetings</DropdownSubTrigger>
						<DropdownContent align='start' sideOffset={4} styles={[styleSheet.subContent]}>
							<DropdownItem onClick={() => insertMergeField(MeetingHomepageLinkPlaceholder.symbol)}>
								Meeting Link
							</DropdownItem>
						</DropdownContent>
					</DropdownSubMenu>
				)}

				{(canRequestFiles || canSendFileLinks) && (
					<DropdownSubMenu>
						<DropdownSubTrigger>Files</DropdownSubTrigger>
						<DropdownContent align='start' sideOffset={4} styles={[styleSheet.subContent]}>
							{canRequestFiles && (
								<DropdownItem onClick={() => insertMergeField(FileRequestLinkPlaceholder.symbol)}>
									File Request Link
								</DropdownItem>
							)}
							{canSendFileLinks && (
								<DropdownItem onClick={() => insertMergeField(FileSendLinkPlaceholder.symbol)}>
									File Download Link
								</DropdownItem>
							)}
						</DropdownContent>
					</DropdownSubMenu>
				)}
			</DropdownContent>
		</Dropdown>
	);
}
